"use strict";
exports.__esModule = true;
var Promise = require("bluebird");
var errors = require("../../errors");
var guards_1 = require("../../guards");
var types_1 = require("../../types");
var defaults = {
    rejectWithReason: false
};
/**
 * @method createCheckConsistency
 *
 * @memberof module:core
 *
 * @param {Provider} provider - Network provider
 *
 * @return {function} {@link #module_core.checkConsistency `checkConsistency`}
 */
exports.createCheckConsistency = function (_a) {
    var send = _a.send;
    /**
     * Checks if a transaction is _consistent_ or a set of transactions are _co-consistent_, by calling
     * [`checkConsistency`](https://docs.iota.org/iri/api#endpoints/checkConsistency) command.
     * _Co-consistent_ transactions and the transactions that they approve (directly or inderectly),
     * are not conflicting with each other and rest of the ledger.
     *
     * As long as a transaction is consistent it might be accepted by the network.
     * In case a transaction is inconsistent, it will not be accepted, and a reattachment
     * is required by calling [`replayBundle`]{@link #module_core.replayBundle}.
     *
     * @example
     *
     * ```js
     * checkConsistency(tailHash)
     *   .then(isConsistent => {
     *     // ...
     *   })
     *   .catch(err => {
     *     // ...
     *   })
     * ```
     *
     * @example
     * ##### Example with `checkConsistency` & `isPromotable`
     *
     * Consistent transactions might remain pending due to networking issues,
     * or if not referenced by recent milestones issued by
     * [Coordinator](https://docs.iota.org/introduction/tangle/consensus).
     * Therefore `checkConsistency` with a time heuristic can determine
     * if a transaction should be [_promoted_]{@link #module_core.promoteTransaction}
     * or [_reattached_]{@link #module_core.replayBundle}.
     * This functionality is abstracted in [`isPromotable`]{@link #module_core.isPromotable}.
     *
     * ```js
     * const isAboveMaxDepth = attachmentTimestamp => (
     *    // Check against future timestamps
     *    attachmentTimestamp < Date.now() &&
     *    // Check if transaction wasn't issued before last 6 milestones
     *    // Milestones are being issued every ~2mins
     *    Date.now() - attachmentTimestamp < 11 * 60 * 1000
     * )
     *
     * const isPromotable = ({ hash, attachmentTimestamp }) => (
     *   checkConsistency(hash)
     *      .then(isConsistent => (
     *        isConsistent &&
     *        isAboveMaxDepth(attachmentTimestamp)
     *      ))
     * )
     * ```
     *
     * @method checkConsistency
     *
     * @memberof module:core
     *
     * @param {Hash|Hash[]} transactions - Tail transaction hash (hash of transaction
     * with `currentIndex == 0`), or array of tail transaction hashes
     * @param {object} [options] - Options
     * @param {boolean} [options.rejectWithReason] - Enables rejection if state is `false`, with reason as error message
     * @param {Callback} [callback] - Optional callback
     *
     * @return {Promise}
     * @fulfil {boolean} Consistency state of given transaction or co-consistency of given transactions.
     * @reject {Error}
     * - `INVALID_TRANSACTION_HASH`: Invalid transaction hash
     * - Fetch error
     * - Reason for returning `false`, if called with `options.rejectWithReason`
     */
    return function checkConsistency(transactions, options, callback) {
        var rejectWithReason = types_1.getOptionsWithDefaults(defaults)(options || {}).rejectWithReason;
        var tails = types_1.asArray(transactions);
        return Promise.resolve(guards_1.validate([tails, function (arr) { return arr.every(guards_1.isHash); }, errors.INVALID_TRANSACTION_HASH]))
            .then(function () {
            return send({
                command: types_1.IRICommand.CHECK_CONSISTENCY,
                tails: tails
            });
        })
            .then(function (_a) {
            var state = _a.state, info = _a.info;
            if (rejectWithReason && !state) {
                throw new Error(errors.inconsistentTransaction(info));
            }
            return state;
        })
            .asCallback(typeof arguments[1] === 'function' ? arguments[1] : callback);
    };
};
//# sourceMappingURL=createCheckConsistency.js.map